/**
 * Commercial licence reads, certificate output and verification.
 *
 * Coordinates LICENSE with AUTH (the owner record comes from the backend when present)
 * and ENTITLEMENTS (there is no licence without the Commercial Toolkit), plus the browser
 * side-effects the certificate needs — a Blob download, a popup print window and the
 * clipboard.
 */
import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import licenseLogoSrc from '../assets/Gold colour Logo (1)- copy.png?inline';
import { COMMERCIAL_ADDON } from '../data/products';
import { downloadBlobFile, downloadTextFile } from '../lib/download';
import { buildCertificateHtml, generateLicenseNumber, verificationUrl } from '../lib/license';
import { openPrintWindow } from '../lib/printBrand';
import {
  selectAvatarUrl,
  selectProfileEmail,
  selectProfileName,
  useAuthStore,
} from '../stores/authStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { useLicenseStore } from '../stores/licenseStore';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import { licenseApi, type LocalLicenseContext } from '../api/licenseApi';

function qrSrcFor(url: string): Promise<string> {
  return QRCode.toDataURL(url, {
    width: 180,
    margin: 2,
    errorCorrectionLevel: 'M',
    color: { dark: '#C9A24B', light: '#FFFFFF' },
  });
}

export function useLicense() {
  const profileName = useAuthStore(selectProfileName);
  const profileEmail = useAuthStore(selectProfileEmail);
  const avatarUrl = useAuthStore(selectAvatarUrl);
  const ownsCommercial = useEntitlementsStore((s) => s.commercial);
  const purchaseDate = useEntitlementsStore((s) => s.commercialPurchaseDate);
  const songs = useSongsStore((s) => s.songs);

  const version = useLicenseStore((s) => s.version);
  const updatedDate = useLicenseStore((s) => s.updatedDate);
  const activity = useLicenseStore((s) => s.activity);
  const downloadHistory = useLicenseStore((s) => s.downloadHistory);
  const license = useLicenseStore((s) => s.license);
  const licensedSongs = useLicenseStore((s) => s.licensedSongs);
  const agreementHistory = useLicenseStore((s) => s.agreementHistory);
  const certificateZoomed = useLicenseStore((s) => s.certificateZoomed);
  const verifyQuery = useLicenseStore((s) => s.verifyQuery);
  const verification = useLicenseStore((s) => s.verification);
  const addActivity = useLicenseStore((s) => s.addActivity);
  const noteCertificateDownload = useLicenseStore((s) => s.noteCertificateDownload);
  const setCertificateZoomed = useLicenseStore((s) => s.setCertificateZoomed);
  const setVerifyQuery = useLicenseStore((s) => s.setVerifyQuery);
  const verifyRaw = useLicenseStore((s) => s.verify);
  const loadLicense = useLicenseStore((s) => s.loadLicense);
  const loadLicensedSongs = useLicenseStore((s) => s.loadLicensedSongs);
  const loadAgreementHistory = useLicenseStore((s) => s.loadAgreementHistory);

  const showToast = useUiStore((s) => s.showToast);

  const context: LocalLicenseContext = {
    ownsCommercial,
    customerName: profileName,
    email: profileEmail,
    purchaseDate,
    version,
    downloadCount: downloadHistory.length,
  };

  useEffect(() => {
    if (!ownsCommercial) return;
    void loadLicense(context);
    void loadLicensedSongs(context, songs);
    void loadAgreementHistory(context);
    // store actions are stable; re-loading follows the inputs that affect the owner record
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownsCommercial,
    profileName,
    profileEmail,
    purchaseDate,
    version,
    downloadHistory.length,
    songs,
  ]);

  const licenseNumber = license?.licenseNumber ?? generateLicenseNumber(profileEmail);
  const ownerName = license?.customerName ?? profileName;
  const ownerEmail = license?.email ?? profileEmail;
  const issuedDate = license?.issuedDate ?? purchaseDate;
  const verifyUrl = license?.verifyUrl ?? verificationUrl(licenseNumber);
  const productName = license?.product ?? COMMERCIAL_ADDON.name;
  const [qrSrc, setQrSrc] = useState('');

  useEffect(() => {
    let active = true;
    void qrSrcFor(verifyUrl)
      .then((src) => {
        if (active) setQrSrc(src);
      })
      .catch(() => {
        if (active) setQrSrc('');
      });
    return () => {
      active = false;
    };
  }, [verifyUrl]);
  const certificateFields = useMemo(
    () => ({
      licenseNumber,
      customerName: ownerName,
      email: ownerEmail,
      purchaseDate: issuedDate || '—',
      plan: productName,
      verifyUrl,
      qrSrc,
      logoSrc: licenseLogoSrc,
      avatarSrc: avatarUrl ?? undefined,
      downloadCount: downloadHistory.length,
      version: license?.version ?? version,
      status: license?.status ?? 'active',
    }),
    [
      downloadHistory.length,
      avatarUrl,
      issuedDate,
      license?.status,
      license?.version,
      licenseNumber,
      ownerEmail,
      ownerName,
      productName,
      verifyUrl,
      qrSrc,
      version,
    ]
  );

  /** The certificate HTML, rebuilt on demand so it always carries current data. */
  async function buildCertificate(): Promise<string> {
    const embeddedQrSrc = qrSrc || (await qrSrcFor(verifyUrl));
    return buildCertificateHtml({ ...certificateFields, qrSrc: embeddedQrSrc });
  }

  return {
    ownsCommercial,
    license,
    licensedSongs,
    agreementHistory,
    licenseNumber,
    customerName: ownerName,
    email: ownerEmail,
    purchaseDate: issuedDate,
    version: license?.version ?? version,
    /** Falls back to the purchase date, matching the prototype. */
    updatedDate: updatedDate || issuedDate,
    verifyUrl,
    qrSrc,
    activity,
    downloadHistory,
    certificateFields,
    certificateZoomed,
    setCertificateZoomed,
    verifyQuery,
    setVerifyQuery,
    verification,

    /** Downloads the certificate as an HTML file — not a PDF. See Licenses.tsx. */
    async downloadCertificate(): Promise<void> {
      try {
        downloadTextFile(
          `Lyrixsa_AI_Commercial_License_${licenseNumber}.html`,
          await buildCertificate(),
          'text/html;charset=utf-8'
        );
        noteCertificateDownload();
        showToast('Certificate downloaded');
      } catch {
        showToast('Could not prepare the certificate download');
      }
    },

    /** Downloads a server-rendered PDF of the certificate preview. */
    async downloadCertificatePdf(): Promise<void> {
      try {
        const { blob } = await licenseApi.downloadCertificatePdf();
        downloadBlobFile(`Lyrixsa_AI_Commercial_License_${licenseNumber}.pdf`, blob);
        noteCertificateDownload();
        showToast('Certificate PDF downloaded');
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Could not generate the certificate PDF'
        );
      }
    },

    /** Opens the original certificate design after all printable assets are ready. */
    async printCertificate(): Promise<void> {
      try {
        if (!openPrintWindow(await buildCertificate())) {
          showToast('Allow pop-ups for this site to print your certificate');
          return;
        }
        noteCertificateDownload();
      } catch {
        showToast('Could not prepare the certificate for printing');
      }
    },

    /** Copies the public verification URL. */
    shareLicenseLink(): void {
      void navigator.clipboard?.writeText(verifyUrl);
      showToast('Verification link copied');
    },

    copyLicenseNumber(): void {
      void navigator.clipboard?.writeText(licenseNumber);
      showToast('License number copied');
    },

    /** Runs a PUBLIC verification lookup by licence number, with optional song checks. */
    async verify(input: { licenseNumber: string; title?: string; slug?: string }): Promise<void> {
      await verifyRaw(input);
    },

    /** Verifies the account's own licence, as the "Verify License" button does. */
    async selfVerify(): Promise<void> {
      setVerifyQuery(licenseNumber);
      addActivity('License Verified', 'verify');
      await verifyRaw({ licenseNumber });
    },
  };
}
